import { BdcDocumentFormat, BdcDocumentShipment, BdcDocumentType } from "../../../types/bdc.type";
import { BDC_DOCUMENTS_MAX_SHIPMENTS } from "../constants";
import { compareLineNo } from "../format";

/** The documents of ศูนย์เอกสาร, in the order the server stacks them per shipment. */
export const BDC_DOCUMENTS: { type: BdcDocumentType; label: string; hint: string; sheet: boolean }[] = [
  { type: "route_summary", label: "ใบคุมสรุปตามสาย (คนขับ)", hint: "1 หน้าต่อ WB × รอบ แนวนอน", sheet: true },
  { type: "store_control_sheet", label: "ใบคุมรายร้าน", hint: "หน้าใหม่ทุกสาย", sheet: true },
  { type: "invoice", label: "INV ต้นฉบับ + สำเนา", hint: "ต้องออก INV แล้ว", sheet: false },
  {
    type: "store_slip",
    label: "ใบส่งสินค้ารายร้าน (A5)",
    hint: "1 หน้าต่อร้าน · พิมพ์แยกไฟล์ ใส่ถาด A5 · ทีละ DC",
    sheet: false,
  },
];

/** Stores one A5 run may hold — the server refuses more. */
export const BDC_STORE_SLIP_MAX_STORES = 3000;

/** The A5 slip prints on its own paper, so it never shares a file with the A4 documents. */
export const isExclusiveDocument = (type: BdcDocumentType): boolean => type === "store_slip";

/** Ticking a document: the A5 slip and the A4 documents replace each other. */
export const toggleDocument = (
  current: ReadonlySet<BdcDocumentType>,
  type: BdcDocumentType,
): Set<BdcDocumentType> => {
  if (current.has(type)) {
    const next = new Set(current);
    next.delete(type);
    return next;
  }
  if (isExclusiveDocument(type)) return new Set([type]);
  const next = new Set(Array.from(current).filter((d) => !isExclusiveDocument(d)));
  next.add(type);
  return next;
};

/** "101, 102 103" → [101, 102, 103]; null when any part is not a whole number. */
export const parseLineNos = (text: string): number[] | null => {
  const parts = text
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.some((part) => !/^\d+$/.test(part))) return null;
  return Array.from(new Set(parts.map(Number)));
};

export const documentLabel = (type: BdcDocumentType): string =>
  BDC_DOCUMENTS.find((d) => d.type === type)?.label ?? type;

/** Why a shipment cannot give a document it does not list in `available_docs`. */
export const unavailableReason = (shipment: BdcDocumentShipment, type: BdcDocumentType): string => {
  if (type === "invoice") return shipment.status === "draft" || shipment.status === "do_confirmed" ? "ยังไม่ออก INV" : "ไม่มี INV";
  if (shipment.kind === "X") return "ไม่มีรายร้าน";
  return "ไม่มีข้อมูล";
};

/** WB → รอบ, the order the board lists them (a kind-X PO after the rounds of its DC). */
export const sortDocumentShipments = (rows: BdcDocumentShipment[]): BdcDocumentShipment[] =>
  rows
    .slice()
    .sort(
      (a, b) =>
        (a.branch_code ?? "").localeCompare(b.branch_code ?? "") ||
        a.dc_code.localeCompare(b.dc_code) ||
        compareLineNo(a.round ?? 99, b.round ?? 99) ||
        a.po_number.localeCompare(b.po_number),
    );

export interface DocumentPlanEntry {
  shipment: BdcDocumentShipment;
  included: BdcDocumentType[];
  skipped: { type: BdcDocumentType; reason: string }[];
}

/** What the file will hold: per ticked shipment (in list order), the chosen documents it has. */
export const planDocuments = (
  shipments: BdcDocumentShipment[],
  selected: ReadonlySet<string>,
  docs: ReadonlySet<BdcDocumentType>,
): DocumentPlanEntry[] =>
  sortDocumentShipments(shipments)
    .filter((s) => selected.has(s.shipment_uuid))
    .map((shipment) => {
      const chosen = BDC_DOCUMENTS.map((d) => d.type).filter((type) => docs.has(type));
      return {
        shipment,
        included: chosen.filter((type) => shipment.available_docs.includes(type)),
        skipped: chosen
          .filter((type) => !shipment.available_docs.includes(type))
          .map((type) => ({ type, reason: unavailableReason(shipment, type) })),
      };
    });

export const pageCount = (plan: DocumentPlanEntry[]): number =>
  plan.reduce((sum, entry) => sum + entry.included.length, 0);

/**
 * Why the chosen output cannot be made, or null. PDF takes any mix of the A4 documents; the A5
 * store slip prints alone, for one DC and at most 3,000 stores; Excel is one sheet document
 * (route summary or per-store) and never the INV; at most 100 shipments; something must print.
 */
export const outputProblem = (
  plan: DocumentPlanEntry[],
  docs: ReadonlySet<BdcDocumentType>,
  format: BdcDocumentFormat,
): string | null => {
  if (plan.length === 0) return "เลือก shipment อย่างน้อย 1 รายการ";
  if (docs.size === 0) return "เลือกเอกสารอย่างน้อย 1 อย่าง";
  if (plan.length > BDC_DOCUMENTS_MAX_SHIPMENTS) return `เลือกได้ครั้งละไม่เกิน ${BDC_DOCUMENTS_MAX_SHIPMENTS} shipment`;
  if (docs.has("store_slip")) {
    if (format === "xlsx") return "ใบส่งสินค้ารายร้านมีแต่ PDF";
    const dcCodes = new Set(plan.map((entry) => entry.shipment.dc_code));
    if (dcCodes.size > 1) return "ใบส่งสินค้ารายร้าน (A5) เลือก shipment ได้ทีละ DC";
    const stores = plan.reduce((sum, entry) => sum + entry.shipment.store_count, 0);
    if (stores > BDC_STORE_SLIP_MAX_STORES)
      return `ใบส่งสินค้ารายร้านพิมพ์ได้ครั้งละไม่เกิน ${BDC_STORE_SLIP_MAX_STORES.toLocaleString()} ร้าน — เลือกสายหรือรอบให้น้อยลง`;
  }
  if (format === "xlsx") {
    if (docs.has("invoice")) return "INV ไม่มีแบบ Excel";
    if (docs.size !== 1) return "Excel เลือกได้ครั้งละหนึ่งเอกสาร";
  }
  if (pageCount(plan) === 0) return "shipment ที่เลือกไม่มีเอกสารที่เลือก";
  return null;
};

/** `?select=a,b` → the uuids it names (blank parts dropped). */
export const parseSelectParam = (value: string | null): string[] =>
  (value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
